/**
 * Auth token plumbing: caches the latest HMAC signature and refreshes it
 * lazily before the 5-minute HMAC window expires. Works with either a
 * direct callback (programmatic) or a URL (declarative) — customer picks
 * whichever fits their stack.
 */
import type { AuthProvider, AuthToken } from './types.js';

const SAFETY_MARGIN_SECONDS = 60;

export class AuthManager {
  /** Cached token keyed by sessionId — signatures bind to a single session. */
  private token: { sessionId: string; value: AuthToken } | null = null;
  private inflight: Promise<AuthToken | null> | null = null;
  /** Bumped whenever provider/endpoint change so an in-flight fetch from the
   *  previous config can't poison the cache after it resolves. */
  private generation = 0;

  constructor(
    private provider: AuthProvider | null,
    private endpoint: string | null,
  ) {}

  /**
   * Swap the provider after construction. Required by the React wrapper:
   * `connectedCallback` fires before React's `useEffect`, so the element
   * boots with `authProvider: null` and we have to inject the provider
   * once it's available. Invalidates the cached token + in-flight fetch.
   */
  setProvider(provider: AuthProvider | null): void {
    this.provider = provider;
    this.token = null;
    this.inflight = null;
    this.generation++;
  }

  /** Same idea for the URL-based path — kept symmetric so element-level
   *  attribute updates propagate too. */
  setEndpoint(endpoint: string | null): void {
    this.endpoint = endpoint;
    this.token = null;
    this.inflight = null;
    this.generation++;
  }

  /**
   * Returns a valid token or null if auth isn't configured (public mode).
   * Never throws — a failure to fetch a signature should surface to the
   * caller as "no token" and let the ingress return 401 with a clear
   * reason rather than crashing the widget.
   */
  async get(sessionId: string): Promise<AuthToken | null> {
    if (!this.provider && !this.endpoint) return null;
    if (!sessionId) return null;

    const now = Math.floor(Date.now() / 1000);
    if (
      this.token &&
      this.token.sessionId === sessionId &&
      this.token.value.ts + 300 > now + SAFETY_MARGIN_SECONDS
    ) {
      return this.token.value;
    }

    if (this.inflight) return this.inflight;

    const myGen = this.generation;
    this.inflight = this.fetchFresh(sessionId).then((tok) => {
      // Drop the result if setProvider/setEndpoint was called mid-fetch —
      // otherwise the cache would carry a token derived from the old config.
      if (myGen !== this.generation) return tok;
      this.token = tok ? { sessionId, value: tok } : null;
      this.inflight = null;
      return tok;
    }).catch(() => {
      if (myGen === this.generation) this.inflight = null;
      return null;
    });

    return this.inflight;
  }

  private async fetchFresh(sessionId: string): Promise<AuthToken | null> {
    if (this.provider) {
      const result = await this.provider({ sessionId });
      return normalizeToken(result);
    }
    if (this.endpoint) {
      const res = await fetch(this.endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      });
      if (!res.ok) return null;
      const raw = await res.json();
      return normalizeToken(raw);
    }
    return null;
  }
}

function normalizeToken(raw: unknown): AuthToken | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const sig = typeof obj.sig === 'string' ? obj.sig : null;
  const ts = typeof obj.ts === 'number' ? obj.ts : typeof obj.ts === 'string' ? parseInt(obj.ts, 10) : null;
  if (!sig || !ts) return null;
  return { sig, ts };
}
